import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { AlgorithmStatusType } from '~/enums/AlgorithmStatusType';

import EcCard from '../../Shared/EcCard/EcCard';
import WarningIcon from '../../Shared/Icons/WarningIcon';
import styles from './KeyInformation.styles';
import {
  filterSectionsByName,
  lowConfidenceFieldsIds,
} from './KeyInformation.utils';
import KeyInformationSection from './KeyInformationSection/KeyInformationSection';

class KeyInformation extends Component {
  constructor(props) {
    super(props);

    this.handleOnFilterChange = this.handleOnFilterChange.bind(this);
    this.handleOnShowFlaggedClick = this.handleOnShowFlaggedClick.bind(this);
    this.state = {
      filterValue: '',
      showFlaggedOnly: false,
      showOCR: null,
      childSections: [],
    };
  }

  componentDidMount() {
    this.setState({ sections: this.props.sections });
  }

  static getDerivedStateFromProps(nextProps) {
    return { sections: nextProps.sections, showOCR: nextProps.showOCR };
  }

  handleOnFilterChange(filterValue) {
    this.setState({ filterValue });
    this.expandChildSections();
  }

  handleOnShowFlaggedClick() {
    this.setState({ showFlaggedOnly: !this.state.showFlaggedOnly });
    this.expandChildSections();
  }

  expandChildSections() {
    this.state.childSections.forEach((child) => child.expandSection());
  }

  render() {
    const {
      classes,
      intl,
      OCRStatus,
      userVisibilityLevel,
      isTerminated,
      onFieldTokenClick,
      ecCardContentStyles,
      isPdfHighlighter,
      queued,
    } = this.props;
    const { showOCR, sections, filterValue, showFlaggedOnly, childSections } =
      this.state;
    const flaggedFieldIds = lowConfidenceFieldsIds(sections);
    const sectionsToShow = filterValue
      ? filterSectionsByName(sections, filterValue)
      : sections;
    const fieldResultsCount = sectionsToShow.reduce(
      (prev, curr) => [...prev, ...curr.fields],
      [],
    ).length;

    let buttonText = 'Show Flagged';
    if (showFlaggedOnly) buttonText = 'Show All';

    return (
      <ContentContainer
        loadingContent={{
          isLoading: OCRStatus === AlgorithmStatusType.InProgress || queued,
          message: intl.formatMessage({
            id: 'KeyInformation.processingMessage',
            defaultMessage: 'Evisort AI is processing the file…',
            description: 'Loading message when AI is processing the file',
          }),
        }}
      >
        <EcCard
          title={intl.formatMessage({
            id: 'KeyInformation.title',
            defaultMessage: 'Key Information',
            description: 'Title for key information panel',
          })}
          contentStyles={ecCardContentStyles}
        >
          <div className={classes.keyInformationSearch}>
            <SearchInput
              ariaDescribedby="key-info-fields-found-status"
              name="key-info-search-input"
              value={filterValue}
              onChange={(filterValue) => this.handleOnFilterChange(filterValue)}
              placeholder={intl.formatMessage({
                id: 'DocumentViewerPageKeyInformationPanel.searchPlaceholder',
                defaultMessage: 'Search key information…',
                description:
                  'Placeholder text for document contents key information search input',
              })}
            />
          </div>
          <div
            className="screenReaderText"
            aria-live="assertive"
            id="key-info-fields-found-status"
            role="status"
          >
            {!filterValue.length
              ? null
              : fieldResultsCount
                ? intl.formatMessage(
                    {
                      id: 'KeyInformation.itemsFoundStatus',
                      defaultMessage: '{count} item(s) found',
                      description:
                        'Status message showing number of items found. Example: count = "5"',
                    },
                    { count: fieldResultsCount },
                  )
                : intl.formatMessage({
                    id: 'KeyInformation.noItemsFoundStatus',
                    defaultMessage: 'No items found',
                    description: 'Status message when no items are found',
                  })}
          </div>
          {flaggedFieldIds.length ? (
            <div className={classes.flaggedInfo}>
              <div className={classes.flaggedWarning}>
                <WarningIcon size="20" yellow />
                <span>
                  {intl.formatMessage(
                    {
                      id: 'KeyInformation.lowConfidenceMessage',
                      defaultMessage:
                        'Our algorithms flagged {count} fields with low confidence.',
                      description:
                        'Warning message about low confidence fields. Example: count = "5"',
                    },
                    { count: flaggedFieldIds.length },
                  )}
                </span>
              </div>
              <div className={classes.flaggedActions}>
                <button onClick={this.handleOnShowFlaggedClick}>
                  {buttonText}
                </button>
              </div>
            </div>
          ) : null}
          <div className={classes.sectionsWrapper}>
            {sectionsToShow.map((section) => (
              <KeyInformationSection
                showOCR={showOCR}
                userVisibilityLevel={userVisibilityLevel}
                isTerminated={isTerminated}
                section={section}
                key={section.title}
                showFlaggedOnly={showFlaggedOnly}
                onRef={(ref) => childSections.push(ref)}
                onFieldTokenClick={onFieldTokenClick}
                isPdfHighlighter={isPdfHighlighter}
              />
            ))}
          </div>
        </EcCard>
      </ContentContainer>
    );
  }
}

export default injectSheet(styles)(injectIntl(KeyInformation));
